import styled from 'styled-components';
import { IconType } from 'react-icons';
import { FaBars, FaCar, FaCarSide, FaCheckCircle, FaChevronDown, FaClipboardCheck, FaDollarSign, FaExclamationCircle, FaFileSignature, FaInfo, FaMoneyBillWave, FaOilCan, FaPercentage, FaUserClock, FaVideo } from 'react-icons/fa';
import { FiBell } from 'react-icons/fi';
import { BarraStatus } from '../celular-locatario/CelularLocatario';
import { App, Moldura, Tela } from '../celular-locatario/styles';

export interface TotaisDashboard {
    trocasDeOleo: number;
    vistorias: number;
    locacoesAtivas: number;
    veiculosDisponiveis: number;
    emLocacao: number;
    investido: number;
    aVencer: number;
    recebidos: number;
    emAtraso: number;
    multas: number;
}

interface Props {
    primeiroNome: string;
    iniciais: string;
    totais: TotaisDashboard;
    escala: number;
}

const dinheiro = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Conteúdo do app do locador, no desenho do app de verdade (cabeçalho, período, cartões com ícone,
// botão de informação, rótulo e valor). A moldura, a barra de status e a escala são as mesmas do
// celular do locatário, para as duas etapas terem a mesma cara. Os números são os da conta.
const Painel = styled.div`
    height: 766px;
    overflow: hidden;
    background: #F4F6FA;
    font-family: 'Roboto', 'Segoe UI', system-ui, -apple-system, sans-serif;
    user-select: none;

    * { box-sizing: border-box; margin: 0; padding: 0; }

    .topo { height: 62px; background: #1e4477; display: flex; align-items: center; justify-content: space-between; padding: 0 12px 0 16px; color: #fff; }
    .topo .esq { display: flex; align-items: center; gap: 16px; font-size: 21px; font-weight: 500; }
    .topo .dir { display: flex; align-items: center; gap: 18px; margin-left: 18px; }
    .sino { position: relative; display: flex; }
    .sino b { position: absolute; top: -9px; right: -12px; min-width: 22px; height: 16px; padding: 0 4px; border-radius: 8px; border: 1.5px solid #fff; background: #1e4477; font-size: 10px; font-weight: 700; line-height: 13px; text-align: center; }
    .avatar { display: flex; align-items: center; gap: 6px; }
    .avatar span { width: 38px; height: 38px; border-radius: 50%; background: #fff; color: #1e4477; font-weight: 700; font-size: 15px; display: flex; align-items: center; justify-content: center; }

    .saudacao { display: flex; align-items: center; justify-content: space-between; padding: 22px 18px 14px; }
    .saudacao small { display: block; font-size: 14px; color: #555; }
    .saudacao strong { font-size: 24px; font-weight: 700; color: #1e4477; }
    .periodo { position: relative; width: 132px; height: 46px; border: 1px solid #333; border-radius: 6px; display: flex; align-items: center; justify-content: space-between; padding: 0 12px; font-size: 15px; color: #222; }
    .periodo i { position: absolute; top: -8px; left: 10px; padding: 0 4px; background: #F4F6FA; font-style: normal; font-size: 11px; color: #555; }

    .titulo-secao { display: flex; align-items: center; gap: 8px; font-size: 19px; font-weight: 700; color: #333; margin: 6px 18px 12px; }
    .titulo-secao span { width: 4px; height: 20px; border-radius: 2px; background: #1e4477; }

    .grade { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 0 14px; }
    .cartao { height: 104px; background: #fff; border-radius: 10px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14); border-left: 5px solid; padding: 10px 10px 10px 12px; display: flex; flex-direction: column; align-items: stretch; text-align: left; }
    .cartao .linha-topo { display: flex; align-items: flex-start; justify-content: space-between; height: 34px; }
    .cartao .info { width: 26px; height: 26px; border-radius: 50%; border: 1.5px solid #1e4477; color: #1e4477; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15); }
    .cartao .rotulo { margin-top: 8px; font-size: 10.5px; letter-spacing: 0.04em; color: #666; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cartao .valor { font-size: 16px; font-weight: 700; color: #222; white-space: nowrap; }

    .calendario { margin-top: 18px; }
    .calendario p { margin: -6px 18px 0; font-size: 12px; color: #777; }
`;

const INDICADORES: { titulo: string; cor: string; Icone: IconType; valor: (t: TotaisDashboard) => string }[] = [
    { titulo: 'Troca de Óleo', cor: '#F39C2C', Icone: FaOilCan, valor: t => String(t.trocasDeOleo) },
    { titulo: 'Vistorias', cor: '#3A9B8A', Icone: FaClipboardCheck, valor: t => String(t.vistorias) },
    { titulo: 'Locações Ativas', cor: '#3B82F6', Icone: FaFileSignature, valor: t => String(t.locacoesAtivas) },
    { titulo: 'Veículos Disponíveis', cor: '#5CAE59', Icone: FaCar, valor: t => String(t.veiculosDisponiveis) },
    { titulo: 'Em Locação', cor: '#3F51B5', Icone: FaCarSide, valor: t => String(t.emLocacao) },
    { titulo: 'Investido', cor: '#9C27B0', Icone: FaMoneyBillWave, valor: t => dinheiro(t.investido) },
    { titulo: 'A Vencer', cor: '#F5C542', Icone: FaUserClock, valor: t => dinheiro(t.aVencer) },
    { titulo: 'Recebidos', cor: '#4CAF50', Icone: FaCheckCircle, valor: t => dinheiro(t.recebidos) },
    { titulo: 'Em Atraso', cor: '#E74C3C', Icone: FaExclamationCircle, valor: t => dinheiro(t.emAtraso) },
    { titulo: 'Multas', cor: '#795548', Icone: FaPercentage, valor: t => dinheiro(t.multas) },
];

export default function DashboardLocador({ primeiroNome, iniciais, totais, escala }: Props) {
    return (
        <Moldura $escala={escala}>
            <Tela $escala={escala}>
                <App $escala={escala}>
                    <BarraStatus clara />
                    <Painel>
                        <div className="topo">
                            <div className="esq"><FaBars size={22} /> Dashboard</div>
                            <div className="dir">
                                <FaDollarSign size={22} />
                                <FaVideo size={21} />
                                <span className="sino"><FiBell size={23} /><b>9+</b></span>
                                <div className="avatar"><span>{iniciais}</span><FaChevronDown size={11} /></div>
                            </div>
                        </div>

                        <div className="saudacao">
                            <div>
                                <small>Bem vindo, {primeiroNome.toUpperCase()}</small>
                                <strong>Visão Geral</strong>
                            </div>
                            <div className="periodo"><i>Período</i>Ano Atual <FaChevronDown size={12} /></div>
                        </div>

                        <div className="titulo-secao"><span />Geral</div>
                        <div className="grade">
                            {INDICADORES.map(({ titulo, cor, Icone, valor }) => (
                                <div className="cartao" key={titulo} style={{ borderLeftColor: cor }}>
                                    <div className="linha-topo">
                                        <Icone size={24} color={cor} />
                                        <span className="info"><FaInfo size={10} /></span>
                                    </div>
                                    <div className="rotulo">{titulo}</div>
                                    <div className="valor">{valor(totais)}</div>
                                </div>
                            ))}
                        </div>

                        <div className="calendario">
                            <div className="titulo-secao"><span />Calendário de Recebimentos</div>
                            <p>Acompanhe as entradas recebidas, em atraso e a vencer da sua locadora</p>
                        </div>
                    </Painel>
                </App>
            </Tela>
        </Moldura>
    );
}
